import { CustomDialogTitle, useSnackbar } from "@/features/ui";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  IconButton,
} from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { PageContainer } from "@/features/ui";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/features/authentication";
import {
  AddChartOfAccountFormDialog,
  chartOfAccountTypeLabels,
  EditChartOfAccountFormDialog,
} from "@/features/chart-of-accounts";
import { PermissionEnum } from "@/features/permissions";
import { apiClient, getErrorMessage } from "@/features/shared";

export const ChartOfAccounts = () => {
  const { hasPermission } = useAuth();
  const { showSnackbar } = useSnackbar();

  const [chartOfAccounts, setChartOfAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [addDialogDefaultValues, setAddDialogDefaultValues] = useState(null);
  const [chartOfAccountId, setChartOfAccountId] = useState(null);
  const [deleteDialogAccount, setDeleteDialogAccount] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Function to fetch all chart of accounts
  const fetchChartOfAccounts = async () => {
    try {
      setLoading(true);

      const {
        data: { data: chartOfAccounts },
      } = await apiClient.get("chart-of-accounts");

      // Filter out virtual root (parent_id is null)
      const filteredAccounts = chartOfAccounts.filter(
        (account) => account.parentId !== null,
      );

      setChartOfAccounts(filteredAccounts);
    } catch (error) {
      console.error("Failed to fetch chart of accounts:", error);
      setChartOfAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChartOfAccounts();
  }, []);

  // Function to delete a chart of account
  const handleDelete = async () => {
    if (!deleteDialogAccount) return;

    try {
      setIsDeleting(true);
      await apiClient.delete(`chart-of-accounts/${deleteDialogAccount.id}`);
      showSnackbar("Akun berhasil dihapus", "success");
      setDeleteDialogAccount(null);
      fetchChartOfAccounts();
    } catch (error) {
      console.error("Failed to delete chart of account:", error);

      // Show error message from backend or fallback message
      showSnackbar(getErrorMessage(error, "Gagal menghapus akun"));
    } finally {
      setIsDeleting(false);
    }
  };

  // Check if a node is a leaf (no children) in nested set model
  const isLeafNode = (row) => {
    return row.rgt - row.lft === 1;
  };

  // Build tree paths from nested set model (lft/rgt)
  const chartOfAccountsWithPaths = useMemo(() => {
    if (!chartOfAccounts.length) return [];

    // Sort by lft to ensure proper order
    const sortedRows = [...chartOfAccounts].sort((a, b) => a.lft - b.lft);

    // Build paths for each row based on nested set model
    return sortedRows.map((row) => {
      const path = [];

      // Find all ancestors (nodes that contain this node)
      sortedRows.forEach((potentialAncestor) => {
        if (
          potentialAncestor.lft < row.lft &&
          potentialAncestor.rgt > row.rgt
        ) {
          path.push(potentialAncestor.code);
        }
      });

      // Add current node to path
      path.push(row.code);

      return {
        ...row,
        path: path,
      };
    });
  }, [chartOfAccounts]);

  const toolbarContent = hasPermission(
    PermissionEnum.CREATE_CHART_OF_ACCOUNTS,
  ) && (
    <Button
      fullWidth
      variant="contained"
      onClick={() => setIsAddDialogOpen(true)}
    >
      Tambah
    </Button>
  );

  const columns = [
    {
      field: "name",
      headerName: "Nama",
      flex: 2,
    },
    {
      field: "type",
      headerName: "Tipe Akun",
      flex: 1,
      renderCell: ({ value }) => chartOfAccountTypeLabels[value],
    },
    {
      field: "deactivatedAt",
      headerName: "Status",
      flex: 1,
      renderCell: ({ value }) => {
        const isDeactivated = Boolean(value);
        return (
          <Chip
            label={isDeactivated ? "Nonaktif" : "Aktif"}
            color={isDeactivated ? "default" : "success"}
          />
        );
      },
    },
  ];

  if (
    hasPermission(PermissionEnum.CREATE_CHART_OF_ACCOUNTS) ||
    hasPermission(PermissionEnum.UPDATE_CHART_OF_ACCOUNTS) ||
    hasPermission(PermissionEnum.DELETE_CHART_OF_ACCOUNTS)
  ) {
    columns.push({
      field: "actions",
      type: "actions",
      headerName: "Aksi",
      width: 150,
      getActions: (params) => {
        const actions = [];

        if (hasPermission(PermissionEnum.CREATE_CHART_OF_ACCOUNTS)) {
          actions.push(
            <IconButton
              key="add"
              onClick={() => {
                setAddDialogDefaultValues({
                  hasParent: true,
                  parentChartOfAccount: params.row,
                  code: `${params.row.code}.`,
                  type: params.row.type,
                });
                setIsAddDialogOpen(true);
              }}
              size="small"
            >
              <AddIcon />
            </IconButton>,
          );
        }

        if (hasPermission(PermissionEnum.UPDATE_CHART_OF_ACCOUNTS)) {
          actions.push(
            <IconButton
              key="edit"
              onClick={() => setChartOfAccountId(params.row.id)}
              size="small"
            >
              <EditIcon />
            </IconButton>,
          );
        }

        // Only show delete for leaf nodes (no children)
        if (
          hasPermission(PermissionEnum.DELETE_CHART_OF_ACCOUNTS) &&
          isLeafNode(params.row)
        ) {
          actions.push(
            <IconButton
              key="delete"
              onClick={() => setDeleteDialogAccount(params.row)}
              size="small"
            >
              <DeleteIcon />
            </IconButton>,
          );
        }

        return actions;
      },
    });
  }

  return (
    <PageContainer
      maxWidth="xl"
      breadcrumbs={[]}
      slotProps={{
        header: { slotProps: { toolbar: { children: toolbarContent } } },
      }}
    >
      <DataGridPro
        rows={chartOfAccountsWithPaths}
        loading={loading}
        autoHeight
        treeData
        getTreeDataPath={(row) => row.path}
        defaultGroupingExpansionDepth={-1}
        groupingColDef={{
          headerName: "Kode",
          flex: 2,
        }}
        columns={columns}
        hideFooterRowCount
        showToolbar
        disableRowSelectionOnClick
      />

      {isAddDialogOpen && (
        <AddChartOfAccountFormDialog
          defaultValues={addDialogDefaultValues}
          onClose={() => {
            setIsAddDialogOpen(false);
            setAddDialogDefaultValues(null);
          }}
          onSubmitted={() => {
            setIsAddDialogOpen(false);
            setAddDialogDefaultValues(null);
            fetchChartOfAccounts();
          }}
        />
      )}

      {!!chartOfAccountId && (
        <EditChartOfAccountFormDialog
          onClose={() => setChartOfAccountId(null)}
          chartOfAccountId={chartOfAccountId}
          onSubmitted={() => {
            setChartOfAccountId(null);
            fetchChartOfAccounts();
          }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {!!deleteDialogAccount && (
        <Dialog
          open
          onClose={() => !isDeleting && setDeleteDialogAccount(null)}
          maxWidth="xs"
          fullWidth
        >
          <CustomDialogTitle
            title="Hapus Akun"
            onClose={() => !isDeleting && setDeleteDialogAccount(null)}
          />
          <DialogContent>
            <DialogContentText>
              Apakah Anda yakin ingin menghapus akun{" "}
              <strong>{deleteDialogAccount.name}</strong> (
              {deleteDialogAccount.code})?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDeleteDialogAccount(null)}
              disabled={isDeleting}
            >
              Batal
            </Button>
            <Button
              onClick={handleDelete}
              variant="contained"
              color="error"
              disabled={isDeleting}
            >
              {isDeleting ? "Menghapus..." : "Hapus"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </PageContainer>
  );
};
